import React, { useEffect, useState } from "react";
import {
	Phone,
	PhoneOff,
	PhoneIncoming,
	PhoneOutgoing,
	PhoneForwarded,
	Circle,
	Mic,
	MicOff,
	Pause,
	Square,
	Play,
	Loader2,
	AlertTriangle,
	Bot,
	ChevronDown,
} from "lucide-react";

import { Button } from "../ui/button";
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogDescription,
} from "../ui/dialog";
import {
	DropdownMenu,
	DropdownMenuTrigger,
	DropdownMenuContent,
	DropdownMenuItem,
} from "../ui/dropdown-menu";
import { useChamada } from "../../context/Chamada/ChamadaContext";
import ModalDeTransferencia from "./ModalDeTransferencia";

// Fase C4 — a chamada na tela: modal de quem está chamando e barra da
// chamada no ar. Fica montada no layout inteiro, e não numa página: chamada
// entra enquanto a pessoa está em qualquer tela.

const formatarNumero = (numero) => {
	const d = String(numero || "").replace(/\D/g, "");

	if (d.length === 13) return `+${d.slice(0, 2)} (${d.slice(2, 4)}) ${d.slice(4, 9)}-${d.slice(9)}`;
	if (d.length === 12) return `+${d.slice(0, 2)} (${d.slice(2, 4)}) ${d.slice(4, 8)}-${d.slice(8)}`;

	return d ? `+${d}` : "número desconhecido";
};

const doisDigitos = (n) => String(n).padStart(2, "0");

const Cronometro = () => {
	const [segundos, setSegundos] = useState(0);

	useEffect(() => {
		const t = setInterval(() => setSegundos((s) => s + 1), 1000);
		return () => clearInterval(t);
	}, []);

	return (
		<span className="tabular-nums">
			{doisDigitos(Math.floor(segundos / 60))}:{doisDigitos(segundos % 60)}
		</span>
	);
};

const Identificacao = ({ chamada }) => (
	<div className="min-w-0">
		<p className="truncate font-medium">
			{chamada.contact?.name || formatarNumero(chamada.phoneNumber)}
		</p>
		{chamada.contact?.name ? (
			<p className="truncate text-xs text-muted-foreground">
				{formatarNumero(chamada.phoneNumber)}
			</p>
		) : null}
	</div>
);

const ModalEntrando = () => {
	const { entrando, atender, recusar, atendendo, atenderComIA, agentesDeVoz } =
		useChamada();

	if (!entrando) return null;

	return (
		// Fechar o modal (o X, o Esc) é recusar: deixar o botão fechar sem
		// avisar o outro lado deixaria o telefone do cliente tocando sozinho.
		// Clique fora não conta — seria recusa por acidente.
		<Dialog open onOpenChange={(aberto) => !aberto && recusar()}>
			<DialogContent className="sm:max-w-sm" onInteractOutside={(e) => e.preventDefault()}>
				<DialogHeader>
					<DialogTitle className="flex items-center gap-2">
						<PhoneIncoming className="h-5 w-5 animate-pulse text-emerald-500" />
						Chamada entrando
					</DialogTitle>
					<DialogDescription>
						{entrando.fromAi
							? `${entrando.fromAi.voiceAgentName || "O agente de voz"} está passando esta ligação para você. Ao atender, a IA sai da linha.`
							: "Atenda para falar pelo microfone deste computador."}
					</DialogDescription>
				</DialogHeader>

				<div className="py-2">
					<Identificacao chamada={entrando} />
				</div>

				{entrando.fromAi?.motivo ? (
					<p className="rounded-md bg-muted px-3 py-2 text-sm">
						<span className="text-muted-foreground">Motivo: </span>
						{entrando.fromAi.motivo}
					</p>
				) : null}

				<div className="flex gap-2">
					<Button
						className="flex-1 bg-emerald-600 hover:bg-emerald-700"
						onClick={atender}
						disabled={atendendo}
					>
						{atendendo ? (
							<Loader2 className="h-4 w-4 animate-spin" />
						) : (
							<Phone className="h-4 w-4" />
						)}
						Atender
					</Button>
					<Button
						variant="destructive"
						className="flex-1"
						onClick={recusar}
						disabled={atendendo}
					>
						<PhoneOff className="h-4 w-4" />
						Recusar
					</Button>
				</div>

				{/* Sem agente ativo o botão não aparece (não fica desabilitado):
				    botão morto é promessa que a tela não cumpre. */}
				{agentesDeVoz?.length && !entrando.fromAi && !entrando.transferFrom ? (
					<DropdownMenu>
						<DropdownMenuTrigger asChild>
							<Button variant="outline" className="w-full" disabled={atendendo}>
								<Bot className="h-4 w-4" />
								Atender com IA
								<ChevronDown className="h-4 w-4" />
							</Button>
						</DropdownMenuTrigger>
						<DropdownMenuContent align="center" className="w-56">
							{agentesDeVoz.map((agente) => (
								<DropdownMenuItem
									key={agente.id}
									onClick={() => atenderComIA(agente.id)}
								>
									{agente.name}
								</DropdownMenuItem>
							))}
						</DropdownMenuContent>
					</DropdownMenu>
				) : null}
			</DialogContent>
		</Dialog>
	);
};

const BarraDaChamada = () => {
	const {
		ativa,
		encerrar,
		silenciar,
		espera,
		gravar,
		estadoMidia,
		transferir,
		cancelarTransferencia,
		transferenciaPendente,
		assumirDaIA,
	} = useChamada();
	const [modalAberto, setModalAberto] = useState(false);
	const [assumindo, setAssumindo] = useState(false);

	if (!ativa) return null;

	const chamando = ativa.status === "ringing";
	// Atendida por agente de voz: o microfone daqui está fechado, e mostrar
	// mudo/espera/gravar/transferir seria oferecer controle sobre um áudio que
	// não passa por este navegador.
	const porIA = Boolean(ativa.voiceAgent);
	const pendente =
		transferenciaPendente && transferenciaPendente.callId === ativa.callId
			? transferenciaPendente
			: null;

	return (
		<div className="fixed bottom-4 right-4 z-50 w-[min(22rem,calc(100vw-2rem))] rounded-lg border bg-card p-4 shadow-lg">
			<div className="flex items-center gap-3">
				{porIA ? (
					<Bot className="h-5 w-5 shrink-0 text-primary" />
				) : ativa.direction === "outgoing" ? (
					<PhoneOutgoing className="h-5 w-5 shrink-0 text-primary" />
				) : (
					<PhoneIncoming className="h-5 w-5 shrink-0 text-emerald-500" />
				)}
				<Identificacao chamada={ativa} />
			</div>

			{porIA ? (
				<p className="mt-2 text-xs text-muted-foreground">
					<span className="font-medium text-foreground">
						{ativa.voiceAgent.name}
					</span>{" "}
					está atendendo. Seu microfone está fechado — assuma a chamada para
					falar.
				</p>
			) : null}

			<p className="mt-2 text-xs text-muted-foreground">
				{chamando ? (
					"Chamando…"
				) : (
					<>
						Em chamada · <Cronometro />
					</>
				)}
				{estadoMidia === "aguardando_microfone"
					? " · aguardando o microfone (libere no navegador)…"
					: null}
				{estadoMidia === "reconectando" ? " · reconectando áudio…" : null}
				{estadoMidia === "perdido" ? " · áudio perdido" : null}
				{ativa.onHold ? " · em espera" : null}
				{ativa.recording ? (
					<span className="ml-1 inline-flex items-center gap-1 font-medium text-destructive">
						<Circle className="h-2.5 w-2.5 fill-current" />
						gravando
					</span>
				) : null}
			</p>

			{/* Gravação pedida que NÃO está acontecendo. O círculo vermelho segue
			    a realidade, mas ficar só apagado seria silêncio: quem pediu para
			    gravar acha que gravou, avisa o cliente de que a conversa está
			    sendo gravada, e depois não há arquivo nenhum. Pior que não
			    gravar. */}
			{/* Durante o toque não há mídia, e portanto não há gravação: o
			    alerta aqui seria alarme falso em TODA ligação gravada, e alarme
			    que pisca por nada é alarme que ninguém lê quando é verdadeiro.
			    Enquanto chama, só o aviso neutro. */}
			{chamando && ativa.recordingRequested && !ativa.recording ? (
				<p className="mt-2 text-xs text-muted-foreground">
					A gravação começa quando o áudio abrir.
				</p>
			) : null}

			{!chamando && !ativa.recording && ativa.recordingRequested ? (
				<div className="mt-2 flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/10 p-2">
					<AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />
					<div className="min-w-0 text-xs">
						<p className="font-medium text-destructive">
							Esta chamada NÃO está sendo gravada
						</p>
						<p className="text-muted-foreground">
							A gravação foi pedida e não começou
							{ativa.recordingError ? `: ${ativa.recordingError}` : "."} Não
							diga ao cliente que a conversa está sendo gravada.
						</p>
					</div>
				</div>
			) : null}

			{/* Transferência esperando aceite: a chamada ainda é minha, e desistir
			    tem que ser possível — senão só o timeout de 30 s resolve. */}
			{pendente ? (
				<div className="mt-2 flex items-center gap-2 rounded-md border border-dashed p-2">
					<PhoneForwarded className="h-4 w-4 shrink-0 text-primary" />
					<span className="flex-1 text-xs text-muted-foreground">
						Transferência oferecida, aguardando aceite…
					</span>
					<Button size="sm" variant="ghost" onClick={cancelarTransferencia}>
						Desistir
					</Button>
				</div>
			) : null}

			{porIA ? (
				<div className="mt-3 flex gap-2">
					<Button
						variant="outline"
						size="sm"
						className="flex-1"
						disabled={assumindo}
						onClick={async () => {
							setAssumindo(true);
							await assumirDaIA();
							setAssumindo(false);
						}}
						title="Tirar a IA da linha e falar você mesmo"
					>
						{assumindo ? (
							<Loader2 className="h-4 w-4 animate-spin" />
						) : (
							<Mic className="h-4 w-4" />
						)}
						Assumir chamada
					</Button>
					<Button
						variant="destructive"
						size="sm"
						className="flex-1"
						onClick={encerrar}
						title="Encerrar chamada"
					>
						<PhoneOff className="h-4 w-4" />
						Encerrar
					</Button>
				</div>
			) : (
			<div className="mt-3 flex gap-2">
				<Button
					variant={ativa.muted ? "default" : "outline"}
					size="sm"
					className="flex-1"
					onClick={() => silenciar(!ativa.muted)}
					title={ativa.muted ? "Reativar microfone" : "Silenciar microfone"}
					aria-label={ativa.muted ? "Reativar microfone" : "Silenciar microfone"}
				>
					{ativa.muted ? (
						<MicOff className="h-4 w-4" />
					) : (
						<Mic className="h-4 w-4" />
					)}
				</Button>
				<Button
					variant={ativa.onHold ? "default" : "outline"}
					size="sm"
					className="flex-1"
					onClick={() => espera(!ativa.onHold)}
					title={ativa.onHold ? "Retomar chamada" : "Colocar em espera"}
					aria-label={ativa.onHold ? "Retomar chamada" : "Colocar em espera"}
				>
					{ativa.onHold ? (
						<Play className="h-4 w-4" />
					) : (
						<Pause className="h-4 w-4" />
					)}
				</Button>
				{/* Gravar (LGPD): quem está na conversa liga quando o assunto
				    exige e desliga quando não exige mais. Vermelho aceso porque
				    "está gravando" é informação que a pessoa precisa ver de
				    relance — e avisar o cliente é obrigação dela. */}
				<Button
					variant={ativa.recording ? "destructive" : "outline"}
					size="sm"
					className="flex-1"
					// Pelo estado REAL: com a gravação pedida e não iniciada, o
					// botão tenta de novo em vez de "desligar" o que nunca ligou.
					onClick={() => gravar(!ativa.recording)}
					disabled={chamando}
					title={
						ativa.recording
							? "Parar de gravar (o trecho seguinte não entra no arquivo)"
							: ativa.recordingRequested
								? "Gravação pedida e NÃO iniciada — tentar de novo"
								: "Gravar esta chamada"
					}
					aria-label={
						ativa.recording ? "Parar de gravar" : "Gravar esta chamada"
					}
				>
					{ativa.recording ? (
						<Square className="h-4 w-4" />
					) : (
						<Circle className="h-4 w-4" />
					)}
				</Button>
				{/* Só faz sentido com a chamada no ar: transferir quem ainda está
				    chamando passaria adiante um telefone tocando. */}
				<Button
					variant="outline"
					size="sm"
					className="flex-1"
					onClick={() => setModalAberto(true)}
					disabled={chamando || Boolean(pendente)}
					title="Transferir chamada"
					aria-label="Transferir chamada"
				>
					<PhoneForwarded className="h-4 w-4" />
				</Button>
				<Button
					variant="destructive"
					size="sm"
					className="flex-1"
					onClick={encerrar}
					title="Encerrar chamada"
					aria-label="Encerrar chamada"
				>
					<PhoneOff className="h-4 w-4" />
				</Button>
			</div>
			)}

			<ModalDeTransferencia
				aberto={modalAberto}
				callId={ativa.callId}
				aoFechar={() => setModalAberto(false)}
				aoTransferir={transferir}
			/>
		</div>
	);
};

const CamadaDeChamada = () => (
	<>
		<ModalEntrando />
		<BarraDaChamada />
	</>
);

export default CamadaDeChamada;
